import React from 'react'

import { RHFInput } from 'react-hook-form-input'
import * as yup from 'yup'
import useForm from 'react-hook-form'

import {
  TextField,
  Typography,
  Paper,
  RadioGroup,
  FormControlLabel,
  Radio,
  makeStyles,
} from '@material-ui/core'

import { InputPhone, InputCep, InputCnpj, InputCpf } from 'components'
import * as service from 'service'
import constants from './../../../../../constants'
import CustomerStaticForm from './../../CustomerStaticForm'
import Summary from './../Summary'
import Footer from './../Footer'
import helpers from 'helpers'
import useBeforeunload from 'hooks/useBeforeunload'
import useWindowSize from 'hooks/useWindowSize'
import useSubscribe from 'hooks/useSubscribe'
import styles from './styles'
import schemas from './../../../schemas'

const useStyles = makeStyles(styles)

const Contractor = () => {
  const classes = useStyles()
  const windowSize = useWindowSize()
  const subscribe = useSubscribe()
  const [isLoading, setIsLoading] = React.useState(false)

  const { register, setValue, handleSubmit, errors, watch } = useForm({
    validationSchema:
      !subscribe.authenticated && !subscribe.authenticatedWithEmail
        ? schemas.subscribeStepContractor
        : schemas.subscribeStepContractor.shape({
            cpf: yup
              .string()
              .cpf()
              .cpfAvailable({
                currentCpf: helpers.people.isPerson(subscribe.customer.kind)
                  ? subscribe.customer.cpf
                  : null,
                kindRef: yup.ref('kind'),
              })
              .when('kind', {
                is: helpers.people.isPerson,
                then: yup.string().required(),
              }),
            cnpj: yup
              .string()
              .cnpj()
              .when('kind', {
                is: helpers.people.isCompany,
                then: yup
                  .string()
                  .cnpjAvailable({
                    currentCnpj: helpers.people.isCompany(
                      subscribe.customer.kind,
                    )
                      ? subscribe.customer.cnpj
                      : null,
                  })
                  .required(),
              }),
            password: null,
            passwordConfirmation: null,
          }),

    defaultValues: {
      ...subscribe.customer,
      phone: subscribe.customer.contact
        ? helpers.people.parsePhoneApi(subscribe.customer.contact)
        : '',
      kind:
        (subscribe.customer && subscribe.customer.kind) ||
        constants.people.PERSON,
      cpf: (subscribe.customer && subscribe.customer.cpf) || '',
      cnpj: (subscribe.customer && subscribe.customer.cnpj) || '',
      companyName: (subscribe.customer && subscribe.customer.companyName) || '',
      tradeName: (subscribe.customer && subscribe.customer.tradeName) || '',
    },
  })

  useBeforeunload(event => {
    if (
      subscribe.editData ||
      (!subscribe.authenticated && !subscribe.authenticatedWithEmail)
    ) {
      return 'Sair do site?'
    }
  })

  const values = watch()

  const isCompany = helpers.people.isCompany(values.kind)

  const handleChangeKind = () => {
    setValue('cnpj', '')
    setValue('tradeName', '')
    setValue('companyName', '')
  }

  const handleRequestCEP = data => {
    if (!data) {
      return
    }

    setValue('address.city', data.address.city, true)
    setValue('address.state', data.address.state, true)
    setValue('address.street', data.address.street, true)
    setValue('address.neighborhood', data.address.neighborhood, true)
  }

  const handleClickPrevButton = () => {
    service.storage.removeRecurrenceAuthTokenCustomer()
    subscribe.setCustomer({ email: subscribe.customer.email })
    subscribe.setAuthenticated(false)
    subscribe.setAuthenticatedWithEmail(false)
    subscribe.setRequireLogin(false)
    subscribe.backStep()
  }

  const handleClickNextButton = event => {
    if (
      (subscribe.authenticated && !subscribe.editData) ||
      subscribe.authenticatedWithEmail
    ) {
      event.preventDefault()
      subscribe.nextStep()
    }
  }

  const handleClickEdit = event => {
    if (subscribe.authenticatedWithEmail) {
      subscribe.forceLogin()

      return
    }

    subscribe.setEditData(true)
  }

  const onSubmit = data => {
    if (!subscribe.authenticated && !subscribe.authenticatedWithEmail) {
      createCustomer(data)
      return
    }

    if (subscribe.editData) {
      updateCustomer(data)
      return
    }
  }

  const createCustomer = async data => {
    try {
      setIsLoading(true)

      await service.recurrence.customers.create({
        name: data.name,
        createPasswordByEmail: false,
        email: data.email,
        companyName: data.companyName,
        tradeName: data.tradeName,
        kind: data.kind,
        cpf: data.cpf,
        cnpj: data.cnpj,
        password: data.password,
        addressAttributes: data.address,
        contactAttributes: helpers.people.convertPhoneApi(data.phone),
      })

      await subscribe.login({ email: data.email, password: data.password })

      setIsLoading(false)

      subscribe.setEditData(false)
      subscribe.nextStep()
    } catch (error) {
      setIsLoading(false)
    }
  }

  const updateCustomer = async data => {
    try {
      setIsLoading(true)

      const responseCustomer = await service.recurrence.customers.put({
        clientId: subscribe.customer.id,
        name: data.name,
        createPasswordByEmail: false,
        email: data.email,
        companyName: data.companyName,
        tradeName: data.tradeName,
        kind: data.kind,
        cpf: data.cpf,
        cnpj: data.cnpj,
        password: data.password,
        addressAttributes: data.address,
        contactAttributes: helpers.people.convertPhoneApi(data.phone),
      })

      await subscribe.setCustomer(responseCustomer.data.customer)

      setIsLoading(false)

      subscribe.setEditData(false)
      subscribe.nextStep()
    } catch (error) {
      setIsLoading(false)
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className={classes.container}>
        <Paper
          elevation={windowSize.isDesktop ? 1 : 0}
          className={classes.content}
        >
          {(!subscribe.authenticated && !subscribe.authenticatedWithEmail) ||
          subscribe.editData ? (
            <>
              <Typography variant="h4" color="textSecondary">
                Seus dados
              </Typography>

              <RHFInput
                as={
                  <RadioGroup className={classes.radioGroup} row>
                    <FormControlLabel
                      value="P"
                      control={<Radio />}
                      label="Pessoa Física"
                    />
                    <FormControlLabel
                      value="C"
                      control={<Radio />}
                      label="Pessoa Jurídica"
                    />
                  </RadioGroup>
                }
                name="kind"
                register={register}
                setValue={setValue}
                mode="onBlur"
                onChange={handleChangeKind}
              />

              <div className={classes.fields}>
                <RHFInput
                  as={
                    <TextField
                      label="Nome"
                      error={!!errors.name}
                      helperText={errors && errors.name && errors.name.message}
                      fullWidth
                    />
                  }
                  name="name"
                  register={register}
                  setValue={setValue}
                  mode="onBlur"
                />

                <RHFInput
                  as={
                    <InputCpf
                      label="CPF"
                      error={!!errors.cpf}
                      helperText={errors && errors.cpf && errors.cpf.message}
                      fullWidth
                    />
                  }
                  name="cpf"
                  register={register}
                  setValue={setValue}
                />
              </div>

              {isCompany && (
                <>
                  <div className={classes.field}>
                    <RHFInput
                      as={
                        <TextField
                          label="Razão social"
                          error={!!errors.companyName}
                          helperText={
                            errors &&
                            errors.companyName &&
                            errors.companyName.message
                          }
                          fullWidth
                        />
                      }
                      name="companyName"
                      register={register}
                      setValue={setValue}
                      mode="onBlur"
                    />
                  </div>

                  <div className={classes.fields}>
                    <RHFInput
                      as={
                        <TextField
                          label="Nome fantasia"
                          error={!!errors.tradeName}
                          helperText={
                            errors &&
                            errors.tradeName &&
                            errors.tradeName.message
                          }
                          fullWidth
                        />
                      }
                      name="tradeName"
                      register={register}
                      setValue={setValue}
                      mode="onBlur"
                    />

                    <RHFInput
                      as={
                        <InputCnpj
                          label="CNPJ"
                          error={!!errors.cnpj}
                          helperText={
                            errors && errors.cnpj && errors.cnpj.message
                          }
                          fullWidth
                        />
                      }
                      name="cnpj"
                      register={register}
                      setValue={setValue}
                    />
                  </div>
                </>
              )}

              <div className={classes.field}>
                <RHFInput
                  as={<TextField label="Email" disabled fullWidth />}
                  name="email"
                  register={register}
                  setValue={setValue}
                />
              </div>

              <div className={classes.field}>
                <RHFInput
                  as={
                    <InputPhone
                      label="Telefone"
                      inputProps={{
                        maxLength: 15,
                      }}
                      error={!!errors.phone}
                      helperText={
                        errors && errors.phone && errors.phone.message
                      }
                      fullWidth
                    />
                  }
                  name="phone"
                  register={register}
                  setValue={setValue}
                  mode="onBlur"
                />
              </div>

              <Typography
                variant="h4"
                color="textSecondary"
                className={classes.titleAddress}
              >
                Endereço
              </Typography>

              <div className={classes.fields}>
                <RHFInput
                  as={
                    <InputCep
                      label="CEP"
                      error={!!errors['address.postalCode']}
                      helperText={
                        errors &&
                        errors['address.postalCode'] &&
                        errors['address.postalCode'].message
                      }
                      onRequest={handleRequestCEP}
                      fullWidth
                    />
                  }
                  name="address.postalCode"
                  register={register}
                  setValue={setValue}
                />
              </div>

              <div className={classes.fields}>
                <RHFInput
                  as={
                    <TextField
                      label="Rua"
                      error={!!errors['address.street']}
                      helperText={
                        errors &&
                        errors['address.street'] &&
                        errors['address.street'].message
                      }
                      fullWidth
                    />
                  }
                  name="address.street"
                  register={register}
                  setValue={setValue}
                  mode="onBlur"
                />

                <RHFInput
                  as={
                    <TextField
                      label="Número"
                      error={!!errors['address.number']}
                      helperText={
                        errors &&
                        errors['address.number'] &&
                        errors['address.number'].message
                      }
                      fullWidth
                    />
                  }
                  name="address.number"
                  register={register}
                  setValue={setValue}
                  mode="onBlur"
                />

                <RHFInput
                  as={
                    <TextField
                      label="Bairro"
                      error={!!errors['address.neighborhood']}
                      helperText={
                        errors &&
                        errors['address.neighborhood'] &&
                        errors['address.neighborhood'].message
                      }
                      fullWidth
                    />
                  }
                  name="address.neighborhood"
                  register={register}
                  setValue={setValue}
                  mode="onBlur"
                />

                <RHFInput
                  as={<TextField label="Complemento (opcional)" fullWidth />}
                  name="address.complement"
                  register={register}
                  setValue={setValue}
                  mode="onBlur"
                />

                <RHFInput
                  as={
                    <TextField
                      label="Estado"
                      error={!!errors['address.state']}
                      helperText={
                        errors &&
                        errors['address.state'] &&
                        errors['address.state'].message
                      }
                      fullWidth
                    />
                  }
                  name="address.state"
                  register={register}
                  setValue={setValue}
                  mode="onBlur"
                />

                <RHFInput
                  as={
                    <TextField
                      label="Cidade"
                      error={!!errors['address.city']}
                      helperText={
                        errors &&
                        errors['address.city'] &&
                        errors['address.city'].message
                      }
                      fullWidth
                    />
                  }
                  name="address.city"
                  register={register}
                  setValue={setValue}
                  mode="onBlur"
                />
              </div>

              {!subscribe.authenticated && (
                <>
                  <div className={classes.fields}>
                    <RHFInput
                      as={
                        <TextField
                          label="Senha"
                          type="password"
                          error={!!errors.password}
                          helperText={
                            errors && errors.password && errors.password.message
                          }
                          fullWidth
                        />
                      }
                      name="password"
                      register={register}
                      setValue={setValue}
                      mode="onBlur"
                    />

                    <RHFInput
                      as={
                        <TextField
                          label="Confirmação de senha"
                          type="password"
                          error={!!errors.passwordConfirmation}
                          helperText={
                            errors &&
                            errors.passwordConfirmation &&
                            errors.passwordConfirmation.message
                          }
                          fullWidth
                        />
                      }
                      name="passwordConfirmation"
                      register={register}
                      setValue={setValue}
                      mode="onBlur"
                    />
                  </div>
                </>
              )}
            </>
          ) : (
            <CustomerStaticForm
              customer={subscribe.customer}
              formatFields={!subscribe.authenticatedWithEmail}
              showEdit={!subscribe.editData}
              onClickEdit={handleClickEdit}
            />
          )}
        </Paper>

        <Summary className={classes.summary} showPlans />
      </div>

      <Footer
        prevButtonText="Voltar"
        prevButtonProps={{ onClick: handleClickPrevButton }}
        nextButtonText={isLoading ? 'Carregando...' : 'Continuar'}
        nextButtonProps={{
          disabled: isLoading,
          onClick: handleClickNextButton,
        }}
      />
    </form>
  )
}

export default Contractor
